import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import logo_ucs from "../../assets/wow-ucskills-logo.svg";
import logo_ap from "../../assets/w-admin-portal.svg";
import {
	getUserFullName,
	getUserResellerName,
	getUserResellerReference,
	getUserResellerType,
	ResellerType
} from "./../../utils/getUserInformation";
import useLocalStorage from "./../../hooks/useLocalStorage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faUsers,
	faStar,
	faPoundSign,
	faBoxesPacking,
	faUser,
	faX,
	faRightFromBracket,
	faPalette
} from "@fortawesome/free-solid-svg-icons";
import { faChartLineUp } from "@fortawesome/pro-solid-svg-icons";
import PermissionGate, { PermissionType } from "../../features/Permissions/PermissionGate";
import "./NavTop.scss";
import { logout } from "../../features/Auth/logout";

const NavTop = () => {
	const [updateStorage, setUpdateStorage] = useState(false);
	window.addEventListener(
		"ap-liu-update-nav",
		() => {
			setUpdateStorage(true);
		},
		false
	);

	const [resellerType, setResellerType] = useState(ResellerType.Invalid);
	const [user, setUser] = useState("");
	const [reseller, setReseller] = useState("");
	const [resellerReference, setResellerReference] = useState("");
	const location = useLocation();

	const topNavMobileBreakingPoint = 992;

	useLocalStorage(
		"ap-liu",
		() => {
			setResellerType(getUserResellerType());
			setUser(getUserFullName());
			setReseller(getUserResellerName());
			setResellerReference(getUserResellerReference());
			setUpdateStorage(false);
		},
		[location, updateStorage]
	);

	const [dropDownIsOpen, setDropDownIsOpen] = useState(false);
	const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
	const { instance } = useMsal();
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);

	const dropDownToggle = () => {
		setDropDownIsOpen((v) => !v)
	};

	const mobileMenuToggle = () => {
		setMobileMenuIsOpen((v) => !v)
	};

	const setActiveClass = (isActive) => {
		return isActive ? "active" : "none";
	};

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		}
	}, []);

	useEffect(() => {
		setMobileMenuIsOpen(false);
	}, [location]);

	return (
		<header className={windowWidth > topNavMobileBreakingPoint ? "topNav" : "topNavMobile"}>
			{/*Backdrop for mobile menu*/}
			<div className={`topNav_backdrop ${mobileMenuIsOpen ? "topNav_backdrop_open" : ""}`}></div>

			{/*Drop Down*/}
			<div
				className={`topNav_dropDown topNav_dropDown_side ${dropDownIsOpen ? "topNav_dropDown_open" : ""
					} ${mobileMenuIsOpen ? "topNav_dropDown_openMobile" : ""}`}
			>
				<div className="topNav_dropDown_text">
					<p className="topNav_dropDown_text_item">
						<b>{user}</b>
					</p>
					<p className="topNav_dropDown_text_item">{reseller}</p>
					{resellerType !== ResellerType.NonBillable && (
						<p className="topNav_dropDown_text_item">Reference: {resellerReference}</p>
					)}
				</div>
				<div className="topNav_dropDown_icons">
					<span onClick={() => logout(instance)} className="topNav_dropDown_icons_icon font_s">
						Logout <FontAwesomeIcon icon={faRightFromBracket} />
					</span>
				</div>
			</div>

			{/*Top Bar*/}
			<div className="topNav_grid">
				<div className="topNav_logo">
					<Link to="/">
						<img alt="ucSKILLS" src={logo_ucs} width="120" height="22" />
						<img alt="Admin Portal" src={logo_ap} width="60" height="22" />
					</Link>
				</div>

				<div className={`topNav_links font_s ${mobileMenuIsOpen ? "topNav_links_open" : ""}`}>
					<PermissionGate
						all={[PermissionType.ManageUcSkillsAccess]}
						when={resellerType === ResellerType.NonBillable || resellerType === ResellerType.Billable}
					>
						<NavLink to="/customers" className={(navData) => setActiveClass(navData.isActive)}>
							<FontAwesomeIcon icon={faStar} />
							<span className="topNav_label">
								{resellerType === ResellerType.Billable
									? "ucSKILLS Access"
									: "My ucSKILLS Customers"}
							</span>
						</NavLink>
					</PermissionGate>

					<PermissionGate all={[PermissionType.ManageAdminUsers]}>
						<NavLink to="/teams" className={(navData) => setActiveClass(navData.isActive)}>
							<FontAwesomeIcon icon={faUsers} />
							<span className="topNav_label">Team</span>
						</NavLink>
					</PermissionGate>

					<PermissionGate all={[PermissionType.ManageResellers]}>
						<NavLink to="/resellers" className={(navData) => setActiveClass(navData.isActive)}>
							<FontAwesomeIcon icon={faBoxesPacking} />
							<span className="topNav_label">Resellers</span>
						</NavLink>
					</PermissionGate>

					<PermissionGate
						all={[PermissionType.AccessInvoices]}
						when={resellerType === ResellerType.iTEL || resellerType === ResellerType.Billable}
					>
						<NavLink to="/billing" className={(navData) => setActiveClass(navData.isActive)}>
							<FontAwesomeIcon icon={faPoundSign} />
							<span className="topNav_label">Billing</span>
						</NavLink>
					</PermissionGate>

					{resellerType === ResellerType.iTEL && (
						<NavLink to="/provisioning" className={(navData) => setActiveClass(navData.isActive)}>
							<FontAwesomeIcon icon={faStar} />
							<span className="topNav_label">Provisioning</span>
						</NavLink>
					)}

					<PermissionGate
						any={[PermissionType.SetWhiteLabelling]}
						when={resellerType === ResellerType.NonBillable || resellerType === ResellerType.Billable}
					>
						<NavLink to="/white-labelling" className={(navData) => setActiveClass(navData.isActive)}>
							<FontAwesomeIcon icon={faPalette} />
							<span className="topNav_label">White Labelling</span>
						</NavLink>
					</PermissionGate>

					<PermissionGate
						any={[PermissionType.ViewReports]}
						when={resellerType === ResellerType.iTEL || resellerType === ResellerType.Billable}
					>
						<NavLink to="/reports" className={(navData) => setActiveClass(navData.isActive)}>
							<FontAwesomeIcon icon={faChartLineUp} />
							<span className="topNav_label">Reports</span>
						</NavLink>
					</PermissionGate>
				</div>

				<div
					className={`topNav_icon ${dropDownIsOpen ? "topNav_icon_active" : ""}`}
					onClick={dropDownToggle}
				>
					<FontAwesomeIcon
						className={`topNav_icon_image ${!dropDownIsOpen ? "topNav_icon_image_active" : ""}`}
						icon={faUser}
					/>
					<FontAwesomeIcon
						className={`topNav_icon_image ${dropDownIsOpen ? "topNav_icon_image_active" : ""}`}
						icon={faX}
					/>
				</div>

				<div
					className={`topNav_hamburger ${mobileMenuIsOpen ? "topNav_hamburger_open" : ""}`}
					onClick={mobileMenuToggle}
				>
					<span className="topNav_hamburger_barOne"></span>
					<span className="topNav_hamburger_barTwo"></span>
					<span className="topNav_hamburger_barThree"></span>
				</div>
			</div>
		</header>
	);
};

export default NavTop;
